import React from 'react'
import { Feature, FeatureCreate } from '../api/client'
import { useCollections } from '../hooks/useCollections'
import {
  BoolParams,
  boolLabels,
  DEFAULT_FALSE_LABEL,
  DEFAULT_TRUE_LABEL,
} from '../utils/boolFeature'

interface FeatureFormProps {
  feature: FeatureCreate | Feature
  setFeature: React.Dispatch<React.SetStateAction<any>>
  onSubmit: (e: React.FormEvent) => Promise<void>
  isEditing?: boolean
  onCancel?: () => void
  submitButtonText?: string
}

interface CollectionPickerProps {
  value: number | undefined
  onChange: (collectionId: number) => void
  collections: Array<{ id: number; name: string; description?: string }>
  collectionsLoading: boolean
}

const CollectionPicker: React.FC<CollectionPickerProps> = ({
  value,
  onChange,
  collections,
  collectionsLoading,
}) => {
  if (collectionsLoading) {
    return (
      <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
        Loading collections...
      </div>
    )
  }
  return (
    <>
      <select
        value={value || ''}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
      >
        <option value="">Select a collection...</option>
        {collections.map((collection) => (
          <option key={collection.id} value={collection.id}>
            {collection.name}
          </option>
        ))}
      </select>
      {collections.length === 0 && (
        <p className="mt-1 text-sm text-gray-500">
          No collections found. You need to create a collection first.
        </p>
      )}
    </>
  )
}

type SubConfig = { type: string; params?: any }

const DEFAULT_BOOL_PARAMS = { true_label: DEFAULT_TRUE_LABEL, false_label: DEFAULT_FALSE_LABEL }

const hasCustomLabels = (params: BoolParams) =>
  (params.true_label ?? DEFAULT_TRUE_LABEL) !== DEFAULT_TRUE_LABEL ||
  (params.false_label ?? DEFAULT_FALSE_LABEL) !== DEFAULT_FALSE_LABEL

// The two answers of a yes/no feature, list item or map half, behind a
// "Custom labels" checkbox: most features keep yes/no, so the inputs only show
// once asked for, and unticking puts the defaults back. Both inputs write into
// the same params object, so a list keeps its `type` alongside.
const BoolLabelsEditor: React.FC<{
  params: BoolParams
  onChange: (next: BoolParams) => void
}> = ({ params, onChange }) => {
  const [custom, setCustom] = React.useState(() => hasCustomLabels(params))
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <input
          type="checkbox"
          checked={custom}
          onChange={(e) => {
            setCustom(e.target.checked)
            if (!e.target.checked) onChange({ ...params, ...DEFAULT_BOOL_PARAMS })
          }}
          className="h-4 w-4 text-amber-800 border-gray-300 rounded focus:ring-action"
        />
        Custom labels
      </label>
      {custom && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            aria-label="Yes answer"
            value={params.true_label ?? ''}
            onChange={(e) => onChange({ ...params, true_label: e.target.value })}
            maxLength={40}
            required
            className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
            placeholder={DEFAULT_TRUE_LABEL}
          />
          <span className="text-gray-400">/</span>
          <input
            type="text"
            aria-label="No answer"
            value={params.false_label ?? ''}
            onChange={(e) => onChange({ ...params, false_label: e.target.value })}
            maxLength={40}
            required
            className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
            placeholder={DEFAULT_FALSE_LABEL}
          />
        </div>
      )}
    </div>
  )
}

const MAP_KEY_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'str', label: 'Text' },
  { value: 'num', label: 'Number' },
  { value: 'enum', label: 'Enum (predefined values)' },
  { value: 'reference', label: 'Collection Item' },
  { value: 'bool', label: 'Yes / No' },
]

const MAP_VALUE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'str', label: 'Text' },
  { value: 'num', label: 'Number' },
  { value: 'enum', label: 'Enum (predefined values)' },
  { value: 'reference', label: 'Collection Item' },
  { value: 'rating', label: 'Rating Scale' },
  { value: 'labeled_rating', label: 'Labeled Rating Scale' },
  { value: 'location', label: 'Location' },
  { value: 'bool', label: 'Yes / No' },
]

const defaultParamsForSubType = (subType: string): any => {
  switch (subType) {
    case 'enum':
      return { type: 'str', values: [] }
    case 'reference':
      return { collection_id: 0 }
    case 'rating':
      return { max: 5 }
    case 'labeled_rating':
      return { max: 5, labels: ['very low', 'low', 'moderate', 'high', 'very high'] }
    case 'bool':
      return { ...DEFAULT_BOOL_PARAMS }
    default:
      return undefined
  }
}

interface MapSideEditorProps {
  sideLabel: 'Key' | 'Value'
  sideHint: string
  subConfig: SubConfig
  setSubConfig: (next: SubConfig) => void
  typeOptions: Array<{ value: string; label: string }>
  collections: Array<{ id: number; name: string; description?: string }>
  collectionsLoading: boolean
}

const MapSideEditor: React.FC<MapSideEditorProps> = ({
  sideLabel,
  sideHint,
  subConfig,
  setSubConfig,
  typeOptions,
  collections,
  collectionsLoading,
}) => {
  const subType = subConfig?.type || 'str'
  const params = subConfig?.params || {}
  const updateParams = (next: any) => setSubConfig({ type: subType, params: next })
  return (
    <div className="border border-gray-200 rounded-md p-3 space-y-3">
      <div>
        <div className="text-sm font-semibold text-gray-700">{sideLabel}</div>
        <div className="text-xs text-gray-500">{sideHint}</div>
      </div>
      <select
        value={subType}
        onChange={(e) => {
          const nextType = e.target.value
          const nextParams = defaultParamsForSubType(nextType)
          setSubConfig(
            nextParams === undefined
              ? { type: nextType }
              : { type: nextType, params: nextParams }
          )
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
      >
        {typeOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {subType === 'num' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unit (optional)
          </label>
          <input
            type="text"
            value={params.unit || ''}
            onChange={(e) => {
              const nextUnit = e.target.value
              // Drop `params` entirely when the unit is cleared, so a
              // unit-less num side stays as `{ type: 'num' }` and doesn't
              // accumulate empty fields.
              if (!nextUnit) {
                const { unit: _drop, ...rest } = params
                void _drop
                if (Object.keys(rest).length === 0) {
                  setSubConfig({ type: subType })
                } else {
                  updateParams(rest)
                }
                return
              }
              updateParams({ ...params, unit: nextUnit })
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
            placeholder={sideLabel === 'Key' ? 'e.g., round, hour, day' : 'e.g., minutes, miles, points'}
          />
        </div>
      )}

      {subType === 'enum' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enum Value Type
            </label>
            <select
              value={params.type || 'str'}
              onChange={(e) =>
                updateParams({ type: e.target.value, values: [] })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
            >
              <option value="str">Text</option>
              <option value="num">Number</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Allowed Values
            </label>
            <div className="space-y-2">
              {(params.values || []).map((value: any, index: number) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type={params.type === 'num' ? 'number' : 'text'}
                    value={value}
                    onChange={(e) => {
                      const next = [...(params.values || [])]
                      next[index] =
                        params.type === 'num'
                          ? parseFloat(e.target.value) || 0
                          : e.target.value
                      updateParams({ ...params, values: next })
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                    placeholder={
                      params.type === 'num' ? 'Enter number' : 'Enter text'
                    }
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...(params.values || [])]
                      next.splice(index, 1)
                      updateParams({ ...params, values: next })
                    }}
                    className="px-2 py-1 bg-danger text-white rounded hover:bg-danger"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const next = [...(params.values || [])]
                  next.push(params.type === 'num' ? 0 : '')
                  updateParams({ ...params, values: next })
                }}
                className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:border-gray-400 hover:text-gray-700"
              >
                + Add Value
              </button>
            </div>
          </div>
        </>
      )}

      {subType === 'bool' && (
        <BoolLabelsEditor params={params} onChange={updateParams} />
      )}

      {subType === 'reference' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Collection
          </label>
          <CollectionPicker
            value={params.collection_id}
            onChange={(collection_id) =>
              updateParams({ ...params, collection_id })
            }
            collections={collections}
            collectionsLoading={collectionsLoading}
          />
        </div>
      )}

      {subType === 'rating' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Highest Rating
          </label>
          <select
            value={params.max || 5}
            onChange={(e) =>
              updateParams({ ...params, max: parseInt(e.target.value) })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
          >
            {[...Array(9)].map((_, i) => (
              <option key={i + 2} value={i + 2}>
                {i + 2}
              </option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">
            Integer scale from 1 to {params.max || 5}
          </p>
        </div>
      )}

      {subType === 'labeled_rating' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Highest Rating
            </label>
            <select
              value={params.max || 5}
              onChange={(e) => {
                const newMax = parseInt(e.target.value)
                const currentLabels: string[] = params.labels || []
                let newLabels = [...currentLabels]
                if (newMax > currentLabels.length) {
                  while (newLabels.length < newMax) newLabels.push('')
                } else if (newMax < currentLabels.length) {
                  newLabels = newLabels.slice(0, newMax)
                }
                updateParams({ ...params, max: newMax, labels: newLabels })
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
            >
              {[...Array(9)].map((_, i) => (
                <option key={i + 2} value={i + 2}>
                  {i + 2}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Labels
            </label>
            <div className="space-y-2">
              {[...Array(params.max || 5)].map((_, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <span className="w-8 text-sm font-medium text-gray-600">
                    {index + 1}:
                  </span>
                  <input
                    type="text"
                    value={params.labels?.[index] || ''}
                    onChange={(e) => {
                      const next = [...(params.labels || [])]
                      next[index] = e.target.value
                      updateParams({ ...params, labels: next })
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                    placeholder={`Label for ${index + 1}`}
                    required
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const FeatureForm: React.FC<FeatureFormProps> = ({
  feature,
  setFeature,
  onSubmit,
  isEditing = false,
  onCancel,
  submitButtonText = 'Save'
}) => {
  const { collections, loading: collectionsLoading } = useCollections()
  return (
    <form onSubmit={onSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title*
          </label>
          <input
            type="text"
            value={feature.title}
            onChange={(e) => setFeature({...feature, title: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
            required
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={feature.description || ''}
            onChange={(e) => setFeature({...feature, description: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
            rows={3}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Value Type
          </label>
          {isEditing ? (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
              {feature.config?.type === 'freeform' && 'Text'}
              {feature.config?.type === 'decimal' && 'Number (decimal)'}
              {feature.config?.type === 'enum' && 'Enum (predefined values)'}
              {feature.config?.type === 'reference' && 'Collection Item'}
              {feature.config?.type === 'list' && 'List (multiple values)'}
              {feature.config?.type === 'map' && 'Map (keys → values)'}
              {feature.config?.type === 'rating' && 'Rating Scale'}
              {feature.config?.type === 'labeled_rating' && 'Labeled Rating Scale'}
              {feature.config?.type === 'bool' && boolLabels(feature.config?.params).join(' / ')}
              {!feature.config?.type && 'Text'}
            </div>
          ) : (
            <select
              value={feature.config?.type || 'freeform'}
              onChange={(e) => {
                const newType = e.target.value;
                // Clear unit if switching from decimal to freeform/enum
                const newUnit = newType === 'decimal' ? feature.unit : '';
                
                // Initialize type-specific config if needed
                let newConfig: any = {type: newType};
                
                // Always reset params when changing types to avoid conflicts
                if (newType === 'enum') {
                  newConfig.params = {type: 'str', values: []};
                } else if (newType === 'reference') {
                  newConfig.params = {collection_id: 0};
                } else if (newType === 'list') {
                  newConfig.params = {type: 'str'};
                } else if (newType === 'map') {
                  newConfig.params = {key: {type: 'str'}, value: {type: 'num'}};
                } else if (newType === 'rating') {
                  newConfig.params = {max: 5};
                } else if (newType === 'labeled_rating') {
                  newConfig.params = {max: 5, labels: ["very low", "low", "moderate", "high", "very high"]};
                } else if (newType === 'bool') {
                  newConfig.params = {...DEFAULT_BOOL_PARAMS};
                } else if (newType === 'freeform' || newType === 'decimal') {
                  // These types don't need params
                  newConfig.params = undefined;
                }
                
                setFeature({
                  ...feature,
                  unit: newUnit,
                  config: newConfig
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
            >
              <option value="freeform">Text</option>
              <option value="decimal">Number (decimal)</option>
              <option value="enum">Enum (predefined values)</option>
              <option value="reference">Collection Item</option>
              <option value="list">List (multiple values)</option>
              <option value="map">Map (keys → values)</option>
              <option value="rating">Rating Scale</option>
              <option value="labeled_rating">Labeled Rating Scale</option>
              <option value="bool">Yes / No</option>
            </select>
          )}
        </div>
        
        {(feature.config?.type === 'decimal' || !feature.config?.type) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit (optional)
            </label>
            <input
              type="text"
              value={feature.unit || ''}
              onChange={(e) => setFeature({...feature, unit: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
              placeholder="e.g., steps, miles, hours"
            />
          </div>
        )}
        
        {feature.config?.type === 'bool' && (
          <div className="md:col-span-2">
            <BoolLabelsEditor
              params={feature.config?.params || {}}
              onChange={(params) => setFeature({
                ...feature,
                config: {...feature.config, params}
              })}
            />
          </div>
        )}

        {feature.config?.type === 'reference' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Collection
            </label>
            <CollectionPicker
              value={feature.config?.params?.collection_id}
              onChange={(collection_id) => setFeature({
                ...feature,
                config: {
                  ...feature.config,
                  params: {...feature.config?.params, collection_id}
                }
              })}
              collections={collections}
              collectionsLoading={collectionsLoading}
            />
          </div>
        )}

        {feature.config?.type === 'map' && (
          <div className="md:col-span-2">
            <p className="text-sm text-gray-500 mb-3">
              When different values apply in different cases, e.g. minutes per exercise (<em>"running": 10, "lifting": 20</em>), or how a group voted (<em>"Tim": "yes", "Kat": "no"</em>).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MapSideEditor
                sideLabel="Key"
                sideHint='e.g. running/lifting or Tim/Kat'
                subConfig={feature.config?.params?.key || {type: 'str'}}
                setSubConfig={(next) => setFeature({
                  ...feature,
                  config: {
                    ...feature.config,
                    params: {...feature.config?.params, key: next}
                  }
                })}
                typeOptions={MAP_KEY_TYPE_OPTIONS}
                collections={collections}
                collectionsLoading={collectionsLoading}
              />
              <MapSideEditor
                sideLabel="Value"
                sideHint={`The corresponding numbers, text, etc., to each key, e.g. 10/30 or "yes"/"no"`}
                subConfig={feature.config?.params?.value || {type: 'num'}}
                setSubConfig={(next) => setFeature({
                  ...feature,
                  config: {
                    ...feature.config,
                    params: {...feature.config?.params, value: next}
                  }
                })}
                typeOptions={MAP_VALUE_TYPE_OPTIONS}
                collections={collections}
                collectionsLoading={collectionsLoading}
              />
            </div>
          </div>
        )}
        
        {feature.config?.type === 'list' && (
          <div className="md:col-span-2">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    List Item Type
                  </label>
                  <select
                    value={feature.config?.params?.type || 'str'}
                    onChange={(e) => {
                      const newListType = e.target.value;
                      let newParams: Record<string, any> = { type: newListType };

                      // Initialize type-specific params
                      if (newListType === 'enum') {
                        newParams = { ...newParams, values: [] };
                      } else if (newListType === 'reference') {
                        newParams = { ...newParams, collection_id: 0 };
                      } else if (newListType === 'bool') {
                        newParams = { ...newParams, ...DEFAULT_BOOL_PARAMS };
                      }

                      // A yes/no list is a run of outcomes, so repeats are
                      // the point: it can't be a set.
                      const { enforce_as_set: _set, ...withoutSet } = feature.config || {};
                      void _set;
                      setFeature({
                        ...feature,
                        config: {
                          ...(newListType === 'bool' ? withoutSet : feature.config),
                          params: newParams
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                  >
                    <option value="str">Text</option>
                    <option value="num">Number</option>
                    <option value="enum">Enum (predefined values)</option>
                    <option value="reference">Collection Item</option>
                    <option value="bool">Yes / No</option>
                  </select>
                </div>
              </div>

              {feature.config?.params?.type === 'bool' && (
                <BoolLabelsEditor
                  params={feature.config?.params}
                  onChange={(params) => setFeature({
                    ...feature,
                    config: {...feature.config, params}
                  })}
                />
              )}

              {feature.config?.params?.type !== 'bool' && (
              <div>
                <label
                  className="flex items-center gap-2"
                  title={isEditing ? 'Cannot be changed after the feature is created.' : undefined}
                >
                  <input
                    type="checkbox"
                    checked={!feature.config?.enforce_as_set}
                    onChange={(e) => setFeature({
                      ...feature,
                      config: {
                        ...feature.config,
                        enforce_as_set: !e.target.checked,
                      }
                    })}
                    disabled={isEditing}
                    className="h-4 w-4 text-amber-800 border-gray-300 rounded focus:ring-action disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className={`text-sm font-medium ${isEditing ? 'text-gray-500' : 'text-gray-700'}`}>
                    Allow repetitions
                  </span>
                </label>
                <p className="mt-1 text-xs text-gray-500 ml-6">
                  {feature.config?.enforce_as_set
                    ? 'Each value appears at most once per checkin.'
                    : 'The same value may appear multiple times in one checkin.'}
                </p>
              </div>
              )}

              {feature.config?.params?.type === 'enum' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed Values
                  </label>
                  <div className="space-y-2">
                    {(feature.config?.params?.values || []).map((value: any, index: number) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => {
                            const newValues = [...(feature.config?.params?.values || [])];
                            newValues[index] = e.target.value;
                            setFeature({
                              ...feature,
                              config: {
                                ...feature.config,
                                params: {...feature.config?.params, values: newValues}
                              }
                            });
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                          placeholder="Enter value"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newValues = [...(feature.config?.params?.values || [])];
                            newValues.splice(index, 1);
                            setFeature({
                              ...feature,
                              config: {
                                ...feature.config,
                                params: {...feature.config?.params, values: newValues}
                              }
                            });
                          }}
                          className="px-2 py-1 bg-danger text-white rounded hover:bg-danger"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      onClick={() => {
                        const newValues = [...(feature.config?.params?.values || [])];
                        newValues.push('');
                        setFeature({
                          ...feature,
                          config: {
                            ...feature.config,
                            params: {...feature.config?.params, values: newValues}
                          }
                        });
                      }}
                      className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:border-gray-400 hover:text-gray-700"
                    >
                      + Add Value
                    </button>
                  </div>
                </div>
              )}

              {feature.config?.params?.type === 'reference' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Collection
                  </label>
                  <CollectionPicker
                    value={feature.config?.params?.collection_id}
                    onChange={(collection_id) => setFeature({
                      ...feature,
                      config: {
                        ...feature.config,
                        params: {...feature.config?.params, collection_id}
                      }
                    })}
                    collections={collections}
                    collectionsLoading={collectionsLoading}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        
        {feature.config?.type === 'enum' && (
          <div className="md:col-span-2">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enum Value Type
                </label>
                <select
                  value={feature.config?.params?.type || 'str'}
                  onChange={(e) => {
                    const newParamType = e.target.value;
                    setFeature({
                      ...feature,
                      config: {
                        ...feature.config,
                        params: {type: newParamType, values: []} // Reset values when changing type
                      }
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                >
                  <option value="str">Text</option>
                  <option value="num">Number</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allowed Values
                </label>
                <div className="space-y-2">
                  {(feature.config?.params?.values || []).map((value: any, index: number) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type={feature.config?.params?.type === 'num' ? 'number' : 'text'}
                        value={value}
                        onChange={(e) => {
                          const newValues = [...(feature.config?.params?.values || [])];
                          const inputValue = feature.config?.params?.type === 'num' ? 
                            parseFloat(e.target.value) || 0 : e.target.value;
                          newValues[index] = inputValue;
                          setFeature({
                            ...feature,
                            config: {
                              ...feature.config,
                              params: {...feature.config?.params, values: newValues}
                            }
                          });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                        placeholder={feature.config?.params?.type === 'num' ? 'Enter number' : 'Enter text'}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newValues = [...(feature.config?.params?.values || [])];
                          newValues.splice(index, 1);
                          setFeature({
                            ...feature,
                            config: {
                              ...feature.config,
                              params: {...feature.config?.params, values: newValues}
                            }
                          });
                        }}
                        className="px-2 py-1 bg-danger text-white rounded hover:bg-danger"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={() => {
                      const newValues = [...(feature.config?.params?.values || [])];
                      const defaultValue = feature.config?.params?.type === 'num' ? 0 : '';
                      newValues.push(defaultValue);
                      setFeature({
                        ...feature,
                        config: {
                          ...feature.config,
                          params: {...feature.config?.params, values: newValues}
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:border-gray-400 hover:text-gray-700"
                  >
                    + Add Value
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {feature.config?.type === 'rating' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Highest Rating
            </label>
            <select
              value={feature.config?.params?.max || 5}
              onChange={(e) => setFeature({
                ...feature,
                config: {
                  ...feature.config,
                  params: {...feature.config?.params, max: parseInt(e.target.value)}
                }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
            >
              {[...Array(9)].map((_, i) => (
                <option key={i + 2} value={i + 2}>{i + 2}</option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">Integer scale from 1 to {feature.config?.params?.max || 5}</p>
          </div>
        )}
        
        {feature.config?.type === 'labeled_rating' && (
          <div className="md:col-span-2">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Highest Rating
                </label>
                <select
                  value={feature.config?.params?.max || 5}
                  onChange={(e) => {
                    const newMax = parseInt(e.target.value);
                    const currentLabels = feature.config?.params?.labels || [];
                    let newLabels = [...currentLabels];
                    
                    // Adjust labels array to match new max
                    if (newMax > currentLabels.length) {
                      // Add empty labels
                      while (newLabels.length < newMax) {
                        newLabels.push('');
                      }
                    } else if (newMax < currentLabels.length) {
                      // Trim labels
                      newLabels = newLabels.slice(0, newMax);
                    }
                    
                    setFeature({
                      ...feature,
                      config: {
                        ...feature.config,
                        params: {...feature.config?.params, max: newMax, labels: newLabels}
                      }
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                >
                  {[...Array(9)].map((_, i) => (
                    <option key={i + 2} value={i + 2}>{i + 2}</option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">Integer scale from 1 to {feature.config?.params?.max || 5}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Labels
                </label>
                <div className="space-y-2">
                  {[...Array(feature.config?.params?.max || 5)].map((_, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <span className="w-8 text-sm font-medium text-gray-600">{index + 1}:</span>
                      <input
                        type="text"
                        value={feature.config?.params?.labels?.[index] || ''}
                        onChange={(e) => {
                          const newLabels = [...(feature.config?.params?.labels || [])];
                          newLabels[index] = e.target.value;
                          setFeature({
                            ...feature,
                            config: {
                              ...feature.config,
                              params: {...feature.config?.params, labels: newLabels}
                            }
                          });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                        placeholder={`Label for ${index + 1}`}
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="md:col-span-2 flex justify-end gap-3 mt-4">
          {isEditing && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="bg-action text-plum px-4 py-2 rounded-md hover:bg-action-dark"
          >
            {submitButtonText}
          </button>
        </div>
      </div>
    </form>
  )
}

export default FeatureForm